// The four views of one response: Answer, Table, Answer flow and JSON. See
// Design > Answer flow, and Design > Exchange, "Consumer API" for the types.

import { Fragment } from "preact";
import type { ComponentChildren } from "preact";
import { Button, CodeBlock, DataTable, List, Notice, Pointer, StatusBadge } from "../../../../ui/src/index.ts";
import type { FeedbackTone, TableColumn } from "../../../../ui/src/index.ts";
import { answersOf, askOf, describeAnswer, hubRows, noAnswerHeadline, shortHub, stepsOf, type AnswerResponse, type AnswerView as AnswerViewModel, type HubRow } from "../../domain/answer.ts";
import { dollarsPerAnswer, perMillion, usd } from "../../domain/money.ts";
import { curlFor } from "../../domain/curl.ts";
import type { AskRequest, NotDispatchedReason, QuestionResult } from "../../domain/types.ts";
import { openReject } from "../actions.ts";
import { useStore } from "../store.ts";

const Bars = ({ ask, bid }: { ask: number | null; bid: number | null }) => {
  if (ask === null) return <span class="cs-muted">Free</span>;
  const top = Math.max(ask, bid ?? 0, 1);
  return (
    <div class="cs-bars">
      <div><span class="cs-bars__label">price</span><span class="cs-bar cs-bar--ask" style={`width:${Math.max(6, (ask / top) * 90)}px`} /><span class="mono">{perMillion(ask)}</span></div>
      {bid !== null && <div><span class="cs-bars__label">bid</span><span class="cs-bar cs-bar--bid" style={`width:${Math.max(6, (bid / top) * 90)}px`} /><span class="mono">{perMillion(bid)}</span></div>}
    </div>
  );
};

const WARN_OUTCOMES = new Set(["PriceTooLow", "MissingParameters", "HubError"]);
const outcomeTone = (o: string): FeedbackTone => (o === "Answer" ? "success" : WARN_OUTCOMES.has(o) ? "warning" : "neutral");

// What each hub did, in plain words; a new outcome fails to compile until it has some.
const DID: Record<HubRow["outcome"], string> = {
  Answer: "Answered", OutOfScope: "Declined", PriceTooLow: "Asks more", MissingParameters: "Needs a detail",
  NotFound: "Found nothing", NotImplemented: "Can't yet", HubError: "Error",
  NotTargeted: "Not asked", BelowMatchCutoff: "Not asked", BelowDispatchThreshold: "Not asked",
  Demonetized: "Not asked", RankedBelowMaxAnswers: "Not asked", TotalCostCap: "Not asked", NoHubSecret: "Not asked",
};
// Why a hub wasn't asked. The Exchange's own words ("dispatch", "hub score") stay in the JSON view.
const NOT_ASKED: Record<NotDispatchedReason, string> = {
  NotTargeted: "Your request named other hubs.",
  BelowMatchCutoff: "Its subject didn't fit your question closely enough.",
  BelowDispatchThreshold: "It matched, but not closely enough to be asked.",
  Demonetized: "It's paused for refunding too many answers.",
  RankedBelowMaxAnswers: "Other hubs matched better, and you asked for fewer answers.",
  TotalCostCap: "Asking it would have gone over your request's cost limit.",
  NoHubSecret: "It can't read its data right now.",
};
const WHY_BY_OUTCOME: Partial<Record<HubRow["outcome"], string>> = { ...NOT_ASKED, Answer: "It matched your question and answered from its data." };
const whyOf = (r: HubRow): string => WHY_BY_OUTCOME[r.outcome] ?? r.note;
const matchOf = (r: HubRow): string | null => (r.score === null ? null : r.threshold === null ? `Match ${r.score}` : `Match ${r.score}, needs ${r.threshold}`);

// A hub that wasn't asked is dimmed in every cell.
const off = (r: HubRow, cell: ComponentChildren) => (r.dispatched ? cell : <span class="cs-off">{cell}</span>);
const HUB_COLUMNS: TableColumn<HubRow>[] = [
  { id: "hub", label: "Hub", render: (r) => off(r, <><strong>{shortHub(r.hub)}</strong>{r.intent && <div class="cs-muted">read it as <span class="mono">{r.intent}</span></div>}</>) },
  { id: "did", label: <span class="cs-nowrap">What it did</span>, render: (r) => off(r, <StatusBadge tone={outcomeTone(r.outcome)}>{DID[r.outcome]}</StatusBadge>) },
  { id: "why", label: "Why", fill: true, render: (r) => off(r, <><span class="cs-note">{whyOf(r)}</span>{matchOf(r) && <div class="mono cs-muted">{matchOf(r)}</div>}</>) },
  { id: "price", label: <span class="cs-nowrap">Its price</span>, render: (r) => off(r, <Bars ask={r.ask} bid={r.bid} />) },
];

export function HubTable({ q }: { q: QuestionResult }) {
  return <DataTable caption="Each hub's response" columns={HUB_COLUMNS} rows={hubRows(q)} rowKey={(r) => r.hub + r.outcome} />;
}

function AnswerCard({ a, q, request }: { a: AnswerResponse; q: QuestionResult; request: AskRequest | null }) {
  const titles = useStore((s) => s.titles);
  const shapes = useStore((s) => s.shapes);
  const refunded = useStore((s) => s.refunded.includes(a.answerId));
  const apiBase = useStore((s) => s.config.apiBase);
  const lessonStep = useStore((s) => {
    const l = s.lesson ? s.content.lessons.find((x) => x.id === s.lesson?.lessonId) : null;
    return l && s.lesson ? l.steps[s.lesson.step]?.kind : undefined;
  });
  const view = describeAnswer(a.answer, titles, shapes, a.shapes);
  const hot = lessonStep === "reject" && !refunded;
  const copy = () => { if (request) void navigator.clipboard?.writeText(curlFor(apiBase, request)).catch(() => undefined); };
  return (
    <article class="cs-answer">
      <div class="cs-answer__main">
        <div class="cs-answer__tags"><StatusBadge tone={refunded ? "neutral" : "success"}>{refunded ? "Refunded" : "Answer"}</StatusBadge><span class="mono cs-muted">from {shortHub(a.hub)} · {a.intent}</span></div>
        {view.headline
          ? <p class={`cs-answer__headline${refunded ? " cs-struck" : ""}`}><span class="mono cs-answer__value">{view.headline.value}</span> <span class="cs-muted">{view.headline.label}</span></p>
          : <p class={`cs-answer__headline${refunded ? " cs-struck" : ""}`}>{view.title}</p>}
        {view.headline?.description && <p class="cs-fact-note">{view.headline.description}</p>}
        <p class="cs-answer__sub">{subtitleOf(view)}</p>
        {view.facts.length > 0 && <dl class="cs-facts">{view.facts.slice(0, 8).map((f) => <Fragment key={f.label}><dt>{f.label}</dt><dd>{f.value}{f.description && <span class="cs-fact-note">{f.description}</span>}</dd></Fragment>)}</dl>}
        {view.members.length > 0 && <ul class="cs-members">{view.members.slice(0, 12).map((m) => <li key={m.ref}>{m.title}{m.detail.map((d) => <StatusBadge key={d}>{d}</StatusBadge>)}</li>)}</ul>}
        {a.provenance.length > 0 && <div class="cs-sources"><span class="cs-label">Sources</span>{a.provenance.map((p) => <span key={p.wref} title={p.wref}><StatusBadge>{p.label}</StatusBadge></span>)}</div>}
      </div>
      <div class="cs-answer__foot">
        <div class="cs-receipt">
          <span>{refunded ? "Refunded" : "Paid"} <strong class="mono">{usd(dollarsPerAnswer(a.price))}</strong></span>
          <span>Took <strong class="mono">{q.ms} ms</strong></span>
          <span>Confidence <strong class="mono">{a.confidence}</strong></span>
        </div>
        <div class="cs-answer__actions">
          <Button size="sm" onClick={copy}>Copy as API call</Button>
          {refunded
            ? <StatusBadge tone="success">Credited {usd(dollarsPerAnswer(a.price))}</StatusBadge>
            : <Pointer active={hot}><Button size="sm" variant="danger" onClick={() => openReject(a.answerId, a.hub, dollarsPerAnswer(a.price))}>Reject for a refund</Button></Pointer>}
        </div>
      </div>
    </article>
  );
}

function HowAnswered({ a, q, request }: { a: AnswerResponse; q: QuestionResult; request: AskRequest | null }) {
  const hubs = new Set([...q.responses.map((r) => r.hub), ...q.notDispatched.map((n) => n.hub)].filter(Boolean));
  const asked = q.responses.filter((r) => r.hub).length;
  const rest = hubs.size - asked;
  const bid = request?.questions?.find((x) => x.id === q.id)?.maxCostPerAnswer;
  const stages = stepsOf(a);
  return (
    <section class="cs-section cs-how" aria-label="How this was answered">
      <h3 class="cs-section__title">How this was answered</h3>
      <ol class="cs-steps">
        <li><span class="cs-step">1</span><div><b>Found the hubs that know the subject</b><p>{hubs.size} {hubs.size === 1 ? "hub" : "hubs"} looked at your question. {asked} matched it closely enough to be asked{rest > 0 ? `; the other ${rest === 1 ? "one wasn't" : `${rest} weren't`} asked and cost nothing` : ""}.</p></div></li>
        <li><span class="cs-step">2</span><div><b>Read it as one of its questions</b><p>{shortHub(a.hub)} read it as <span class="mono">{a.intent}</span>{typeof bid === "number" ? <> and checked your bid ({perMillion(bid)}) against its price ({perMillion(a.price)})</> : null}.</p></div></li>
        <li><span class="cs-step">3</span><div><b>Answered from its data, then checked the answer</b><p>Tool version <span class="mono">{a.toolVersion}</span>, with {a.provenance.length === 1 ? "1 source" : `${a.provenance.length} sources`}.</p></div></li>
        <li><span class="cs-step">4</span><div><b>You paid for one answer</b><p>{usd(dollarsPerAnswer(a.price))}, the hub's price. If it's wrong, reject it and you're refunded at once.</p></div></li>
      </ol>
      {stages.length > 0 && <details class="cs-trace">
        <summary>Trace <span class="cs-muted">· {stages.length} steps, in the order they ran</span></summary>
        {/* A timeline: one dot per step on a line; a step that took no measurable time gets a hollow dot. */}
        <ol>{stages.map((s, i) => <li key={`${i}-${s.stage}`} data-instant={s.ms === 0 ? "" : undefined}>
          <span class="cs-trace__dot" aria-hidden="true" />
          <div><div class="cs-trace__head"><span class="mono cs-trace__stage">{s.stage}</span><span class="mono cs-trace__ms">{s.ms} ms</span></div><p>{s.text}</p></div>
        </li>)}</ol>
      </details>}
    </section>
  );
}

// What the answer is, in a line; a new kind of answer fails to compile until it has one.
const subtitleOf = (view: AnswerViewModel): string => {
  switch (view.kind) {
    case "assertion": return `A ${view.shape} about ${view.about ?? "this answer"}.`;
    case "collection": return `${view.members.length} members.`;
    case "thing": return `A ${view.shape}.`;
    default: return view.kind satisfies never;
  }
};

export function AnswerView({ q, request }: { q: QuestionResult; request: AskRequest | null }) {
  const answers = answersOf(q);
  const [first] = answers;
  if (first) {
    return (
      <div class="cs-answers">
        {answers.map((a) => <AnswerCard key={a.answerId} a={a} q={q} request={request} />)}
        <HowAnswered a={first} q={q} request={request} />
      </div>
    );
  }
  // With no response at all, say it without the Exchange's word "dispatched".
  const head = q.responses.length > 0 ? noAnswerHeadline(q) : { text: "No hub answered.", sub: "No hub matched your question closely enough to be asked. You weren't charged." };
  const missing = q.responses.flatMap((r) => (r._tag === "MissingParameters" ? r.missing : []));
  const askNow = askOf(q);
  return (
    <div class="cs-answers">
      <div class="cs-noanswer"><p class="cs-answer__headline">{head.text}</p><p class="cs-answer__sub">{head.sub}</p></div>
      {missing.length > 0 && (
        <section class="cs-section"><h3 class="cs-section__title">What's missing</h3>
          <dl class="cs-facts">{missing.map((m) => <Fragment key={m.name}><dt class="mono">{m.name}</dt><dd>{m.description}<span class="cs-fact-note mono">Why: {m.reason}</span></dd></Fragment>)}</dl></section>
      )}
      {askNow !== null && <Notice tone="info" title={`Its price is ${perMillion(askNow)}.`}>Raise your bid to at least this and send again.</Notice>}
      <section class="cs-section"><h3 class="cs-section__title">By hub</h3><HubTable q={q} /></section>
    </div>
  );
}

export function FlowView({ q }: { q: QuestionResult }) {
  const rows = hubRows(q);
  const answers = answersOf(q);
  return (
    <div class="cs-flow">
      <section aria-label="Which hubs were asked"><h3><span class="cs-step">1</span>Which hubs know this?</h3>
        <p class="cs-muted">Each hub's subject is compared with your question. Only the hubs that match closely enough are asked; the rest cost nothing.</p>
        <List>
          {rows.map((r) => (
            <div key={r.hub + r.outcome} class={`cs-flow__row${r.dispatched ? "" : " cs-off"}`}>
              <div class="cs-flow__line"><strong>{shortHub(r.hub)}</strong><StatusBadge tone={r.dispatched ? "success" : "neutral"}>{r.dispatched ? "Asked" : "Not asked"}</StatusBadge></div>
              {!r.dispatched && <div class="cs-note">{whyOf(r)}</div>}
              {matchOf(r) && <div class="mono cs-muted">{matchOf(r)}</div>}
            </div>
          ))}
        </List>
      </section>
      <section aria-label="What each hub made of it"><h3><span class="cs-step">2</span>What each hub makes of it</h3>
        <p class="cs-muted">Each hub that was asked reads your question as one of the questions it answers, or declines it, and checks your bid against its price.</p>
        {rows.some((r) => r.dispatched)
          ? <List>{rows.filter((r) => r.dispatched).map((r) => (
            <div key={r.hub + r.outcome} class="cs-flow__row">
              <div class="cs-flow__line"><strong>{shortHub(r.hub)}</strong>{r.intent && <span class="cs-muted">read it as <span class="mono">{r.intent}</span></span>}</div>
              <Bars ask={r.ask} bid={r.bid} /><div class="cs-note">{whyOf(r)}</div>
            </div>
          ))}</List>
          : <p class="cs-flow__none">No hub was asked, so none read the question.</p>}
      </section>
      <section aria-label="Answers"><h3><span class="cs-step">3</span>Answers you paid for</h3>
        <p class="cs-muted">Only answers are charged, at the hub's price. Reject one and you're refunded at once.</p>
        {answers.length > 0
          ? <List>{answers.map((a) => <div key={a.answerId} class="cs-flow__row"><div class="cs-flow__line"><strong>{shortHub(a.hub)}</strong><StatusBadge tone="success">Answer</StatusBadge></div><div>Paid <strong class="mono">{usd(dollarsPerAnswer(a.price))}</strong> · <span class="mono">{q.ms} ms</span></div></div>)}</List>
          : <p class="cs-flow__none">No answers, so nothing was charged.</p>}
      </section>
    </div>
  );
}

export const JsonView = ({ value }: { value: unknown }) => <CodeBlock label="Response JSON" code={JSON.stringify(value, null, 2)} />;
